import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Package, Truck, CheckCircle, Clock, Search, Filter, Loader2, ArrowUpDown, User, Building, FileText, Download, FileSpreadsheet, Phone, Camera, AlertTriangle, MessageSquare, X, Image as ImageIcon, CheckSquare, Square, Plus, Minus, Tag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pickup: { label: 'Picked Up', color: 'bg-blue-100 text-blue-700', icon: Truck },
  processed: { label: 'Processed', color: 'bg-orange-100 text-orange-700', icon: Package },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  delivery_overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const ManageOrders = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pgs, setPgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPG, setFilterPG] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [issueComment, setIssueComment] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Create Order modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pgCustomers, setPgCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newOrderClothes, setNewOrderClothes] = useState(10);
  const [newOrderType, setNewOrderType] = useState('');
  const [newOrderInstructions, setNewOrderInstructions] = useState('');
  const [creatingOrder, setCreatingOrder] = useState(false);
  // New PG customer creation
  const [showNewPGCust, setShowNewPGCust] = useState(false);
  const [newPGCust, setNewPGCust] = useState({ full_name: '', phone: '', password: '', address: '', pg_id: '' });
  const [creatingPGCust, setCreatingPGCust] = useState(false);

  // Delivery assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);
  const [assignType, setAssignType] = useState('delivery'); // 'pickup' | 'delivery'
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loadingBoys, setLoadingBoys] = useState(false);
  const [assignForm, setAssignForm] = useState({ delivery_boy_id: '', scheduled_date: '', notes: '' });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, pgsRes] = await Promise.all([
        axios.get(`${API}/admin/customer-orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pgs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setOrders(ordersRes.data);
      setPgs(pgsRes.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = async () => {
    setShowCreateModal(true);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setNewOrderClothes(10);
    setNewOrderType('');
    setNewOrderInstructions('');
    setShowNewPGCust(false);
    setNewPGCust({ full_name: '', phone: '', password: '', address: '', pg_id: '' });
    if (pgCustomers.length === 0) {
      setLoadingCustomers(true);
      try {
        const res = await axios.get(`${API}/admin/pg-customers/list`, { headers: { Authorization: `Bearer ${token}` } });
        setPgCustomers(res.data);
      } catch {
        toast.error('Failed to load customers');
      } finally {
        setLoadingCustomers(false);
      }
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedCustomer) { toast.error('Please select a customer'); return; }
    if (!newOrderClothes || newOrderClothes < 1) { toast.error('Enter at least 1 clothing item'); return; }
    setCreatingOrder(true);
    try {
      await axios.post(`${API}/admin/pg-orders/create-for-customer`, {
        customer_id: selectedCustomer.id,
        total_clothes: newOrderClothes,
        clothes_type: newOrderType || null,
        special_instructions: newOrderInstructions || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order created for ${selectedCustomer.full_name}`);
      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleCreatePGCustomer = async () => {
    if (!newPGCust.full_name || !newPGCust.phone || !newPGCust.password || !newPGCust.pg_id) {
      toast.error('Fill in name, phone, password, and PG'); return;
    }
    setCreatingPGCust(true);
    try {
      const res = await axios.post(`${API}/admin/create-pg-customer`, {
        full_name: newPGCust.full_name,
        phone: newPGCust.phone,
        password: newPGCust.password,
        address: newPGCust.address || 'N/A',
        pg_id: newPGCust.pg_id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPgCustomers(prev => [res.data, ...prev]);
      setSelectedCustomer(res.data);
      setShowNewPGCust(false);
      setNewPGCust({ full_name: '', phone: '', password: '', address: '', pg_id: '' });
      toast.success(`Customer "${res.data.full_name}" created and selected`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    } finally { setCreatingPGCust(false); }
  };

  const fetchDeliveryBoys = async () => {
    setLoadingBoys(true);
    try {
      const res = await axios.get(`${API}/admin/delivery-boys`, { headers: { Authorization: `Bearer ${token}` } });
      setDeliveryBoys(res.data);
    } catch { toast.error('Failed to load delivery staff'); }
    finally { setLoadingBoys(false); }
  };

  const handleAssignDelivery = async () => {
    if (!assignForm.delivery_boy_id) { toast.error('Select a delivery boy'); return; }
    if (!assignForm.scheduled_date) { toast.error('Pick a scheduled date'); return; }
    setAssigning(true);
    try {
      await axios.post(`${API}/admin/delivery-assignments`, {
        delivery_boy_id: assignForm.delivery_boy_id,
        order_id: assignOrder.id,
        order_type: 'pg',
        assignment_type: assignType,
        scheduled_date: assignForm.scheduled_date,
        notes: assignForm.notes || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${assignType === 'pickup' ? 'Pickup' : 'Delivery'} assigned!`);
      setShowAssignModal(false);
      setAssignOrder(null);
      setAssignForm({ delivery_boy_id: '', scheduled_date: '', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign delivery');
    } finally { setAssigning(false); }
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder || !newStatus) return;
    setUpdating(true);
    try {
      await axios.patch(
        `${API}/admin/customer-orders/${selectedOrder.id}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order updated to ${statusConfig[newStatus].label}`);
      setShowStatusModal(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setShowStatusModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || !selectedOrder) return;
    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
    try {
      await axios.post(`${API}/admin/customer-orders/${selectedOrder.id}/photos`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photos uploaded');
      fetchData();
      setShowPhotoModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resolveIssue = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/customer-orders/${selectedOrder.id}/issue`, {
        status: 'resolved',
        comment: issueComment,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Issue resolved');
      setShowIssueModal(false);
      setIssueComment('');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve issue');
    } finally {
      setUpdating(false);
    }
  };

  const isOverdue = (order) => {
    if (order.status === 'delivered') return false;
    const exp = order.expected_delivery || order.expected_delivery_date;
    if (!exp) return false;
    return new Date(exp) < new Date();
  };

  const toggleSelect = (id) => {
    const order = filteredOrders.find(o => o.id === id);
    if (!order?.photos?.length) { toast.error('Upload photos before changing status'); return; }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const eligible = filteredOrders.filter(o => o.status !== 'delivered' && o.photos?.length > 0);
    if (selectedIds.size === eligible.length && eligible.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligible.map(o => o.id)));
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      const res = await axios.patch(`${API}/admin/customer-orders/bulk-status`, {
        order_ids: Array.from(selectedIds),
        status: bulkStatus,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      setBulkStatus('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filterPG !== 'all' && order.pg_id !== filterPG) return false;
    if (filterStatus !== 'all' && order.status !== filterStatus) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        order.customer_name?.toLowerCase().includes(term) ||
        order.customer_phone?.includes(term) ||
        order.pg_name?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    pickup: orders.filter(o => o.status === 'pickup').length,
    processed: orders.filter(o => o.status === 'processed').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  };

  const getExportData = () => filteredOrders.map((order) => ({
    'Customer': order.customer_name || '',
    'Phone': order.customer_phone || '',
    'PG': order.pg_name || '',
    'Items': order.total_clothes || 0,
    'Type': order.clothes_type || '',
    'Instructions': order.special_instructions || '',
    'Order Date': order.order_date ? format(new Date(order.order_date), 'dd MMM yyyy') : '-',
    'Status': statusConfig[order.status]?.label || order.status,
  }));

  const printTags = async (order) => {
    try {
      const resp = await axios.get(`${API}/admin/customer-orders/${order.id}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('Failed to generate tags');
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const today = format(new Date(), 'dd MMM yyyy');
    
    doc.setFontSize(18);
    doc.setTextColor(67, 56, 202);
    doc.text('JustFold - Order Report', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${today} | ${filteredOrders.length} orders`, 14, 28);

    const rows = getExportData();
    autoTable(doc, {
      head: [Object.keys(rows[0] || {})],
      body: rows.map(r => Object.values(r)),
      startY: 35,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 255] },
    });

    doc.save(`JustFold_Orders_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exported successfully');
  };

  const exportToExcel = () => {
    const rows = getExportData();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    
    // Column widths
    worksheet['!cols'] = [
      { wch: 20 }, { wch: 14 }, { wch: 18 }, { wch: 8 },
      { wch: 20 }, { wch: 25 }, { wch: 14 }, { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `JustFold_Orders_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'bg-slate-100 text-slate-700' },
          { label: 'Pending', value: stats.pending, color: 'bg-yellow-50 text-yellow-700' },
          { label: 'Picked Up', value: stats.pickup, color: 'bg-blue-50 text-blue-700' },
          { label: 'Processed', value: stats.processed, color: 'bg-orange-50 text-orange-700' },
          { label: 'Delivered', value: stats.delivered, color: 'bg-green-50 text-green-700' },
        ].map((s) => (
          <Card key={s.label} className={s.color}>
            <CardContent className="py-3 px-4 text-center">
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs font-medium">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by customer name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-orders-input"
          />
        </div>
        <Select value={filterPG} onValueChange={setFilterPG}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-pg-select">
            <Building className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="All PGs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PGs</SelectItem>
            {pgs.map((pg) => (
              <SelectItem key={pg.id} value={pg.id}>{pg.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-44" data-testid="filter-status-select">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="pickup">Picked Up</SelectItem>
            <SelectItem value="processed">Processed</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToPDF}
            disabled={filteredOrders.length === 0}
            className="h-10 text-red-600 border-red-200 hover:bg-red-50"
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            disabled={filteredOrders.length === 0}
            className="h-10 text-green-600 border-green-200 hover:bg-green-50"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
          <Button
            size="sm"
            onClick={openCreateModal}
            className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1" />
            Create Order
          </Button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-indigo-700">{selectedIds.size} selected</span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-44 bg-white" data-testid="bulk-status-select">
              <SelectValue placeholder="Set status..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="pickup">Picked Up</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={handleBulkUpdate}
            disabled={!bulkStatus || bulkUpdating}
            data-testid="bulk-update-btn"
          >
            {bulkUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="ml-auto text-slate-500">
            Clear
          </Button>
        </div>
      )}

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">No orders found</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile: Card view */}
          <div className="space-y-3 md:hidden">
            {filteredOrders.map((order) => {
              const overdue = isOverdue(order);
              const effectiveStatus = overdue && order.status !== 'delivered' ? 'delivery_overdue' : order.status;
              const cfg = statusConfig[effectiveStatus] || statusConfig.pending;
              const Icon = cfg.icon;
              const expDel = order.expected_delivery || order.expected_delivery_date;
              return (
                <Card key={order.id} className={overdue ? 'border-red-300 bg-red-50/50' : ''} data-testid={`order-card-${order.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-2">
                        {order.status !== 'delivered' && order.photos?.length > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); toggleSelect(order.id); }} className="mt-0.5" data-testid={`select-order-${order.id}`}>
                            {selectedIds.has(order.id) ? <CheckSquare className="w-5 h-5 text-indigo-600" /> : <Square className="w-5 h-5 text-slate-300" />}
                          </button>
                        )}
                        <div>
                          <p className="font-semibold text-slate-800">{order.customer_name}</p>
                          <a href={`tel:${order.customer_phone}`} className="text-xs text-indigo-600 flex items-center gap-1">
                            <Phone className="w-3 h-3" />{order.customer_phone}
                          </a>
                        </div>
                      </div>
                      <Badge className={`${cfg.color} flex items-center gap-1`}>
                        <Icon className="w-3 h-3" />{cfg.label}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                      <div><span className="text-slate-400 text-xs">PG</span><p className="text-slate-700">{order.pg_name}</p></div>
                      <div><span className="text-slate-400 text-xs">Items</span><p className="font-semibold">{order.total_clothes}</p></div>
                      <div><span className="text-slate-400 text-xs">Order</span><p className="text-slate-700">{order.order_date ? format(new Date(order.order_date), 'dd MMM') : '-'}</p></div>
                      <div><span className="text-slate-400 text-xs">Exp. Delivery</span><p className={overdue ? 'text-red-600 font-semibold' : 'text-slate-700'}>{expDel ? format(new Date(expDel), 'dd MMM') : '-'}</p></div>
                    </div>
                    {order.issue && (
                      <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                        <span className="font-semibold text-amber-700">Issue: {order.issue.issue_type}</span>
                        <span className={`ml-2 ${order.issue.status === 'open' ? 'text-red-600' : 'text-green-600'}`}>({order.issue.status})</span>
                      </div>
                    )}
                    <div className="mb-1">
                      <span className="font-mono text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded px-1.5 py-0.5">#{order.id?.slice(0,8).toUpperCase()}</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" className="text-violet-600 border-violet-200" onClick={() => printTags(order)} title="Print Tags">
                        <Tag className="w-3 h-3 mr-1" />Tags
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => openStatusModal(order)} disabled={order.status === 'delivered' || !order.photos?.length} title={!order.photos?.length ? 'Upload photos first' : ''}>
                        <ArrowUpDown className="w-3 h-3 mr-1" />{!order.photos?.length ? 'Upload Photo First' : 'Status'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setSelectedOrder(order); setShowPhotoModal(true); }}>
                        <Camera className="w-3 h-3" />
                      </Button>
                      {order.issue?.status === 'open' && (
                        <Button size="sm" variant="outline" className="text-amber-600" onClick={() => { setSelectedOrder(order); setIssueComment(''); setShowIssueModal(true); }}>
                          <MessageSquare className="w-3 h-3" />
                        </Button>
                      )}
                      {order.status === 'pending' && (
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-200" title="Assign Pickup"
                          onClick={() => { setAssignOrder(order); setAssignType('pickup'); setAssignForm({ delivery_boy_id: '', scheduled_date: new Date().toISOString().split('T')[0], notes: '' }); fetchDeliveryBoys(); setShowAssignModal(true); }}>
                          <Truck className="w-3 h-3" />
                        </Button>
                      )}
                      {['pickup', 'processed'].includes(order.status) && (
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200" title="Assign Delivery"
                          onClick={() => { setAssignOrder(order); setAssignType('delivery'); setAssignForm({ delivery_boy_id: '', scheduled_date: new Date().toISOString().split('T')[0], notes: '' }); fetchDeliveryBoys(); setShowAssignModal(true); }}>
                          <Truck className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop: Table view */}
          <Card className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <button onClick={toggleSelectAll} data-testid="select-all-orders">
                        {selectedIds.size > 0 && selectedIds.size === filteredOrders.filter(o => o.status !== 'delivered' && o.photos?.length > 0).length
                          ? <CheckSquare className="w-4 h-4 text-indigo-600" />
                          : <Square className="w-4 h-4 text-slate-400" />}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Order ID</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Customer</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">PG</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Items</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Order Date</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Exp. Delivery</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Issue</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredOrders.map((order) => {
                    const overdue = isOverdue(order);
                    const effectiveStatus = overdue && order.status !== 'delivered' ? 'delivery_overdue' : order.status;
                    const cfg = statusConfig[effectiveStatus] || statusConfig.pending;
                    const Icon = cfg.icon;
                    const expDel = order.expected_delivery || order.expected_delivery_date;
                    return (
                      <tr key={order.id} className={`transition-colors ${overdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`} data-testid={`order-row-${order.id}`}>
                        <td className="px-3 py-3">
                          {order.status !== 'delivered' && order.photos?.length > 0 ? (
                            <button onClick={() => toggleSelect(order.id)} data-testid={`select-row-${order.id}`}>
                              {selectedIds.has(order.id) ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-300" />}
                            </button>
                          ) : <div className="w-4" />}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded px-1.5 py-0.5">#{order.id?.slice(0,8).toUpperCase()}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{order.customer_name}</p>
                          <a href={`tel:${order.customer_phone}`} className="text-xs text-indigo-600 flex items-center gap-1 hover:underline">
                            <Phone className="w-3 h-3" />{order.customer_phone}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{order.pg_name}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">{order.total_clothes}</td>
                        <td className="px-4 py-3 text-slate-600">{order.order_date ? format(new Date(order.order_date), 'dd MMM yyyy') : '-'}</td>
                        <td className={`px-4 py-3 ${overdue ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                          {expDel ? format(new Date(expDel), 'dd MMM yyyy') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${cfg.color} flex items-center gap-1 w-fit`}>
                            <Icon className="w-3 h-3" />{cfg.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {order.issue ? (
                            <Badge className={order.issue.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                              {order.issue.issue_type} ({order.issue.status})
                            </Badge>
                          ) : <span className="text-slate-400">-</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50" onClick={() => printTags(order)} title={`Print ${order.total_clothes || 1} tags`}>
                              <Tag className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openStatusModal(order)} disabled={order.status === 'delivered' || !order.photos?.length} data-testid={`update-status-${order.id}`} title={!order.photos?.length ? 'Upload photos first' : 'Update status'}>
                              <ArrowUpDown className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedOrder(order); setShowPhotoModal(true); }} title="Photos">
                              <Camera className="w-3 h-3" />
                            </Button>
                            {order.issue?.status === 'open' && (
                              <Button size="sm" variant="outline" className="text-amber-600" onClick={() => { setSelectedOrder(order); setIssueComment(''); setShowIssueModal(true); }} title="Resolve Issue">
                                <MessageSquare className="w-3 h-3" />
                              </Button>
                            )}
                            {order.status === 'pending' && (
                              <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" title="Assign Pickup Boy"
                                onClick={() => { setAssignOrder(order); setAssignType('pickup'); setAssignForm({ delivery_boy_id: '', scheduled_date: new Date().toISOString().split('T')[0], notes: '' }); fetchDeliveryBoys(); setShowAssignModal(true); }}>
                                <Truck className="w-3 h-3" />
                              </Button>
                            )}
                            {['pickup', 'processed'].includes(order.status) && (
                              <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" title="Assign Delivery Boy"
                                onClick={() => { setAssignOrder(order); setAssignType('delivery'); setAssignForm({ delivery_boy_id: '', scheduled_date: new Date().toISOString().split('T')[0], notes: '' }); fetchDeliveryBoys(); setShowAssignModal(true); }}>
                                <Truck className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{selectedOrder.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{selectedOrder.pg_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{selectedOrder.total_clothes} items &mdash; {selectedOrder.clothes_type}</span>
                </div>
              </div>

              {/* Status Progress */}
              <div className="flex items-center justify-between px-2">
                {Object.entries(statusConfig).map(([key, cfg], idx) => {
                  const Icon = cfg.icon;
                  const isActive = Object.keys(statusConfig).indexOf(newStatus) >= idx;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewStatus(key)}
                      className="flex flex-col items-center gap-1 group"
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                        isActive ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400 group-hover:bg-slate-300'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className={`text-[10px] font-medium ${isActive ? 'text-indigo-600' : 'text-slate-400'}`}>
                        {cfg.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleStatusUpdate}
              disabled={updating || newStatus === selectedOrder?.status}
              data-testid="confirm-status-update"
            >
              {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Modal */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-indigo-600" /> Order Photos</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">{selectedOrder.customer_name} &mdash; {selectedOrder.total_clothes} items</p>
              {/* Existing photos */}
              {selectedOrder.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedOrder.photos.map((photo) => (
                    <img key={photo.id} src={`${BACKEND_URL}${photo.url}`} alt="Order" className="w-full h-24 object-cover rounded-lg border" />
                  ))}
                </div>
              )}
              {(!selectedOrder.photos || selectedOrder.photos.length < 5) && (
                <div>
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : (
                      <>
                        <Camera className="w-6 h-6 text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500">Upload photos for verification</span>
                      </>
                    )}
                    <input type="file" multiple accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} data-testid="photo-upload-input" />
                  </label>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Order Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" /> Create Order for Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Customer search */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Select PG Customer</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by name or phone..."
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              {loadingCustomers ? (
                <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
              ) : (
                <div className="border border-slate-200 rounded-xl max-h-40 overflow-y-auto divide-y">
                  {pgCustomers
                    .filter(c => {
                      const t = customerSearch.toLowerCase();
                      return !t || c.full_name?.toLowerCase().includes(t) || c.phone?.includes(t) || c.pg_name?.toLowerCase().includes(t);
                    })
                    .map(c => (
                      <button
                        key={c.id}
                        onClick={() => setSelectedCustomer(c)}
                        className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors ${selectedCustomer?.id === c.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                      >
                        <p className="font-medium text-slate-800 text-sm">{c.full_name}</p>
                        <p className="text-xs text-slate-400">{c.phone} · {c.pg_name}</p>
                      </button>
                    ))}
                  {pgCustomers.length === 0 && !loadingCustomers && (
                    <p className="text-center text-sm text-slate-400 py-4">No customers found</p>
                  )}
                </div>
              )}
              {selectedCustomer && (
                <div className="mt-2 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">{selectedCustomer.full_name}</p>
                    <p className="text-xs text-blue-500">{selectedCustomer.phone} · {selectedCustomer.pg_name}</p>
                  </div>
                </div>
              )}

              {/* New PG Customer inline creation */}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowNewPGCust(v => !v)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {showNewPGCust ? 'Cancel new customer' : 'Create new PG customer'}
                </button>
                {showNewPGCust && (
                  <div className="mt-2 border border-blue-100 rounded-xl p-3 bg-blue-50 space-y-2">
                    <Input
                      placeholder="Full Name *"
                      value={newPGCust.full_name}
                      onChange={e => setNewPGCust(p => ({ ...p, full_name: e.target.value }))}
                      className="bg-white text-sm"
                    />
                    <Input
                      placeholder="Phone *"
                      value={newPGCust.phone}
                      onChange={e => setNewPGCust(p => ({ ...p, phone: e.target.value }))}
                      className="bg-white text-sm"
                    />
                    <Input
                      placeholder="Password *"
                      type="password"
                      value={newPGCust.password}
                      onChange={e => setNewPGCust(p => ({ ...p, password: e.target.value }))}
                      className="bg-white text-sm"
                    />
                    <Input
                      placeholder="Address (optional)"
                      value={newPGCust.address}
                      onChange={e => setNewPGCust(p => ({ ...p, address: e.target.value }))}
                      className="bg-white text-sm"
                    />
                    <select
                      value={newPGCust.pg_id}
                      onChange={e => setNewPGCust(p => ({ ...p, pg_id: e.target.value }))}
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">Select PG *</option>
                      {pgs.map(pg => (
                        <option key={pg.id} value={pg.id}>{pg.name}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={handleCreatePGCustomer}
                      disabled={creatingPGCust}
                    >
                      {creatingPGCust ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create & Select Customer'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Clothes count */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Number of Clothes</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setNewOrderClothes(v => Math.max(1, v - 1))}
                  className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center font-bold transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-2xl font-black text-slate-800 w-10 text-center">{newOrderClothes}</span>
                <button
                  onClick={() => setNewOrderClothes(v => v + 1)}
                  className="w-9 h-9 rounded-xl bg-blue-100 hover:bg-blue-200 flex items-center justify-center font-bold transition-colors"
                >
                  <Plus className="w-4 h-4 text-blue-600" />
                </button>
                <div className="flex gap-1.5 ml-2">
                  {[5, 10, 15, 20].map(n => (
                    <button key={n} onClick={() => setNewOrderClothes(n)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors ${newOrderClothes === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Clothes type */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Clothes Type <span className="text-slate-400 font-normal">(optional)</span></label>
              <Input
                placeholder="e.g. Shirts, Jeans, T-Shirts..."
                value={newOrderType}
                onChange={e => setNewOrderType(e.target.value)}
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Special Instructions <span className="text-slate-400 font-normal">(optional)</span></label>
              <Textarea
                placeholder="Any special care instructions..."
                value={newOrderInstructions}
                onChange={e => setNewOrderInstructions(e.target.value)}
                className="min-h-[72px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreateOrder}
              disabled={creatingOrder || !selectedCustomer}
            >
              {creatingOrder ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Resolve Modal */}
      <Dialog open={showIssueModal} onOpenChange={setShowIssueModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" /> Resolve Issue</DialogTitle>
          </DialogHeader>
          {selectedOrder?.issue && (
            <div className="space-y-4">
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="font-medium text-amber-800">{selectedOrder.issue.issue_type}</p>
                <p className="text-sm text-amber-600 mt-1">{selectedOrder.issue.description}</p>
                <p className="text-xs text-amber-500 mt-2">Raised: {format(new Date(selectedOrder.issue.raised_at), 'dd MMM yyyy, hh:mm a')}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={`tel:${selectedOrder.customer_phone}`} className="flex-1">
                  <Button variant="outline" className="w-full text-indigo-600">
                    <Phone className="w-4 h-4 mr-1" />Call Customer
                  </Button>
                </a>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Resolution Comment</label>
                <Textarea
                  value={issueComment}
                  onChange={(e) => setIssueComment(e.target.value)}
                  placeholder="e.g., Issue fixed, clothes re-checked"
                  className="min-h-[80px]"
                  data-testid="issue-comment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueModal(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={resolveIssue} disabled={updating} data-testid="resolve-issue-btn">
              {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark Resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Delivery Boy Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className={`w-5 h-5 ${assignType === 'pickup' ? 'text-blue-500' : 'text-emerald-500'}`} />
              {assignType === 'pickup' ? 'Assign Pickup Boy' : 'Assign Delivery Boy'}
            </DialogTitle>
          </DialogHeader>
          {assignOrder && (
            <div className="space-y-4 py-1">
              <div className="bg-slate-50 rounded-xl px-4 py-3 text-sm">
                <p className="font-semibold text-slate-800">{assignOrder.customer_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{assignOrder.pg_name}</p>
                <p className="text-xs text-indigo-600 font-medium mt-1">{assignOrder.total_clothes} items</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">Select Delivery Boy</label>
                {loadingBoys ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
                ) : deliveryBoys.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-3">No delivery staff created yet</p>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
                    {deliveryBoys.map(boy => (
                      <button key={boy.id}
                        onClick={() => setAssignForm(f => ({ ...f, delivery_boy_id: boy.id }))}
                        className={`w-full px-4 py-2.5 flex items-center gap-3 text-left border-b border-slate-100 last:border-0 transition-colors ${assignForm.delivery_boy_id === boy.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : 'hover:bg-slate-50'}`}>
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-emerald-700">{boy.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700">{boy.name}</p>
                          <p className="text-xs text-slate-400">{boy.pending_count || 0} active tasks</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">Scheduled Date</label>
                <input type="date" value={assignForm.scheduled_date}
                  onChange={e => setAssignForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">Notes <span className="font-normal text-slate-400 normal-case">(optional)</span></label>
                <textarea value={assignForm.notes}
                  onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Instructions for the delivery boy..."
                  rows={2}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-400" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button className={assignType === 'pickup' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} onClick={handleAssignDelivery} disabled={assigning || !assignForm.delivery_boy_id}>
              {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Truck className="w-4 h-4 mr-1" />}
              {assignType === 'pickup' ? 'Assign Pickup' : 'Assign Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageOrders;
